import React from "react";
import { Link } from "react-router-dom";
import { Shield, Lock } from "lucide-react";
import AppVersionBadge from "../common/AppVersionBadge";
import GetAppButton from "../common/GetAppButton";

// Social Icons
const XIcon = ({ size = 18, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const TikTokIcon = ({ size = 18, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

// Single source of truth for footer link hover — brand-gradient on hover.
const linkCls = "text-gray-400 hover:brand-gradient-text hover:[-webkit-text-fill-color:transparent] text-xs transition";

// Gradient h4 with a 24px brand-gradient underline beneath
const headerCls = "brand-gradient-text font-black text-xs uppercase tracking-[0.18em] mb-2 inline-block relative pb-1 after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-6 after:rounded-full after:brand-gradient-bg";

const Footer = () => {
  return (
    <footer className="hidden md:block bg-black border-t border-white/10 mt-auto">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Main Footer Grid - Compact Layout */}
        <div className="grid grid-cols-5 gap-8 mb-5">
          {/* EXPLORE */}
          <div>
            <h4 className={headerCls}>Explore</h4>
            <ul className="space-y-1">
              <li><Link to="/" className={linkCls}>Home</Link></li>
              <li><Link to="/live" className={linkCls} data-testid="footer-live-breaks-link">Live Breaks</Link></li>
              <li><Link to="/drops" className={linkCls}>Drops</Link></li>
              <li><Link to="/series" className={linkCls}>Series</Link></li>
              <li><Link to="/sets" className={linkCls}>Sets</Link></li>
              <li><Link to="/staking" className={linkCls}>Lock In</Link></li>
              <li><Link to="/challenges" className={linkCls}>Challenges</Link></li>
              <li><Link to="/marketplace" className={linkCls}>Swap Spot</Link></li>
              <li><Link to="/scan" className={linkCls} data-testid="footer-scan-ai-link">Scan AI · Buyback</Link></li>
              <li><Link to="/locker" className={linkCls}>Locker</Link></li>
            </ul>
          </div>

          {/* COMPANY */}
          <div>
            <h4 className={headerCls}>Company</h4>
            <ul className="space-y-1">
              <li><Link to="/about" className={linkCls}>About Us</Link></li>
              <li><Link to="/contact" className={linkCls}>Contact</Link></li>
              <li><Link to="/faq" className={linkCls}>FAQ</Link></li>
              <li><Link to="/shipping" className={linkCls}>Shipping & Returns</Link></li>
            </ul>
          </div>

          {/* ACCOUNT */}
          <div>
            <h4 className={headerCls}>Account</h4>
            <ul className="space-y-1">
              <li><Link to="/profile" className={linkCls}>Profile</Link></li>
              <li><Link to="/wallet" className={linkCls}>Pack Points</Link></li>
              <li><Link to="/claim" className={linkCls}>Claim Your Card</Link></li>
              <li><Link to="/calculator" className={linkCls}>Calculator</Link></li>
              <li><Link to="/login" className={linkCls}>Sign In</Link></li>
            </ul>
          </div>

          {/* LEGAL */}
          <div>
            <h4 className={headerCls}>Legal</h4>
            <ul className="space-y-1">
              <li><Link to="/terms" className={linkCls}>Terms of Service</Link></li>
              <li><Link to="/privacy" className={linkCls}>Privacy Policy</Link></li>
              <li><Link to="/copyright" className={linkCls}>Copyright</Link></li>
              <li><Link to="/licensing" className={linkCls}>Licensing</Link></li>
              <li><Link to="/partnerships" className={linkCls}>Partnerships</Link></li>
              <li><Link to="/report" className={linkCls}>Report Abuse</Link></li>
            </ul>
          </div>

          {/* HEADQUARTERS & SOCIAL */}
          <div>
            <h4 className={headerCls}>Headquarters</h4>
            <address className="text-gray-500 text-xs not-italic leading-relaxed">
              <span className="text-white/85 font-semibold">BLK PACKS™</span><br />
              205 N Michigan Ave, Suite 810<br />
              Chicago, IL 60601
            </address>
            <a href="mailto:support@blkpacks.fun" className="brand-gradient-text font-semibold hover:brightness-110 text-xs mt-1 inline-block transition">
              support@blkpacks.fun
            </a>

            {/* Social Icons */}
            <div className="flex items-center gap-3 mt-3">
              <a
                href="https://x.com/blkpacks"
                target="_blank"
                rel="noopener noreferrer"
                className="w-7 h-7 rounded-full bg-black/60 border border-orange-500/30 hover:border-orange-500/70 flex items-center justify-center text-gray-400 hover:text-orange-400 transition-colors"
              >
                <XIcon size={14} />
              </a>
              <a
                href="https://www.tiktok.com/@blkpacks"
                target="_blank"
                rel="noopener noreferrer"
                className="w-7 h-7 rounded-full bg-black/60 border border-orange-500/30 hover:border-orange-500/70 flex items-center justify-center text-gray-400 hover:text-orange-400 transition-colors"
              >
                <TikTokIcon size={14} />
              </a>
            </div>
          </div>
        </div>

        {/* GET THE APP strip — sits above the legal/copyright row so it's
            the last thing every desktop user sees before the fine print. */}
        <div className="border-t border-white/10 mt-2 pt-4 pb-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
            <div>
              <p className="brand-gradient-text font-black text-sm uppercase tracking-[0.18em]">
                Rip Anywhere. Anytime.
              </p>
              <p className="text-gray-500 text-[11px] mt-1">
                Native iOS + Android app — Drops, AR, Live Breaks, all in your pocket.
              </p>
            </div>
            <GetAppButton size="md" testId="footer-get-app" />
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-white/10 pt-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <p className="text-gray-500 text-[11px]">© 2026 <span className="brand-gradient-text font-semibold">BLK PACKS™</span> — a DBA of Xcraft FunWorks LLC</p>
              <span className="text-gray-700">|</span>
              <p className="text-gray-600 text-[11px]">Entertainment collectibles platform.</p>
              <span className="text-gray-700">|</span>
              <AppVersionBadge compact />
            </div>
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1.5 bg-black/60 border border-orange-500/30 rounded-full px-2.5 py-1">
                <Shield size={12} className="text-orange-400" />
                <span className="brand-gradient-text font-black uppercase tracking-wider text-[10px]">Secure Checkout</span>
              </div>
              <div className="flex items-center gap-1.5 bg-black/60 border border-orange-500/30 rounded-full px-2.5 py-1">
                <Lock size={12} className="text-orange-400" />
                <span className="brand-gradient-text font-black uppercase tracking-wider text-[10px]">SSL Encrypted</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
